import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { HardDrive, Images, Search, Copy, Loader2, UserSearch, ZoomIn, CheckCircle2 } from 'lucide-react';
import { mediaUrl } from '../utils/mediaUrl';
import { MemberPicker } from '../components/MemberSearchPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const AdminMedia = () => {
  const { token } = useAuth();
  const [usage, setUsage] = useState(null);
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [duplicates, setDuplicates] = useState(null);
  const [checkingDupes, setCheckingDupes] = useState(false);
  const [merging, setMerging] = useState({});
  const [orphanedGroups, setOrphanedGroups] = useState(null);
  const [sort, setSort] = useState('date_desc');
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 60;
  const [selectedIds, setSelectedIds] = useState([]);
  const [zoom, setZoom] = useState(null);
  const [assignMember, setAssignMember] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadOrphaned = async () => {
    try {
      const res = await fetch(`${API_URL}/api/media/orphaned-galleries`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setOrphanedGroups(data);
    } catch (e) {
      toast.error('Could not load recovered artwork queue');
    }
  };

  const loadAssets = async (term = '', sortVal = sort, offsetVal = offset) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), sort: sortVal, offset: String(offsetVal) });
      if (term) params.set('search', term);
      const res = await fetch(`${API_URL}/api/media/assets?${params.toString()}`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) { setAssets(data.assets || []); setTotal(data.total || 0); }
    } catch (e) {
      toast.error('Could not load the media library');
    } finally {
      setLoading(false);
    }
  };

  const changeSort = (value) => {
    setSort(value);
    setOffset(0);
    loadAssets(search, value, 0);
  };

  const runSearch = () => {
    setOffset(0);
    loadAssets(search, sort, 0);
  };

  const goToPage = (newOffset) => {
    setOffset(newOffset);
    setSelectedIds([]);
    loadAssets(search, sort, newOffset);
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const assignSelected = async () => {
    if (!assignMember || selectedIds.length === 0) return;
    setAssigning(true);
    try {
      const res = await fetch(`${API_URL}/api/media/orphaned-galleries/claim-images`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_ids: selectedIds, member_id: assignMember.id }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Could not assign these images'); return; }
      toast.success(`Attached ${data.claimed} image(s) to ${data.member}'s gallery`);
      setSelectedIds([]);
      setAssignMember(null);
      loadAssets(search, sort);
      loadOrphaned();
    } catch (e) {
      toast.error('Network error assigning images');
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/media/usage`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setUsage(d))
      .catch(() => {});
    loadAssets();
    loadOrphaned();
    // eslint-disable-next-line
  }, [token]);

  const runDuplicates = async () => {
    setCheckingDupes(true);
    try {
      const res = await fetch(`${API_URL}/api/media/duplicates/review`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setDuplicates(data);
      else toast.error(data.detail || 'Could not build the duplicate report');
    } catch (e) {
      toast.error('Network error building the duplicate report');
    } finally {
      setCheckingDupes(false);
    }
  };

  const mergeGroup = async (etag) => {
    setMerging((m) => ({ ...m, [etag]: true }));
    try {
      const res = await fetch(`${API_URL}/api/media/duplicates/merge`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ etags: [etag] }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Merge failed'); return; }
      const result = data.results?.[0];
      if (result?.merged) {
        toast.success(`Kept 1 copy, removed ${result.removed.length}. ${result.reference_updates} reference(s) repointed.`);
      } else {
        toast.error(result?.reason || 'Nothing to merge');
      }
      runDuplicates();
      loadAssets(search, sort, offset);
      fetch(`${API_URL}/api/media/usage`, { headers: authHeaders }).then((r) => r.ok && r.json()).then((d) => d && setUsage(d));
    } catch (e) {
      toast.error('Network error merging duplicates');
    } finally {
      setMerging((m) => ({ ...m, [etag]: false }));
    }
  };

  const creditsPercent = usage?.credits_used_percent ?? null;

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-media-page">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-stone-900">Media Library</h1>
          <p className="text-sm text-stone-500 mt-1">
            One image, one stored original. Re-uploading the same photo reuses what is already there.
          </p>
        </div>

        {/* Storage visibility */}
        <Card className="mb-6" data-testid="media-usage-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <HardDrive size={17} /> Storage Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usage ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-stone-500">Storage used</p>
                    <p className="font-semibold text-stone-900" data-testid="usage-storage">{formatBytes(usage.storage_bytes)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Tracked in library</p>
                    <p className="font-semibold text-stone-900" data-testid="usage-tracked">{usage.tracked_assets}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Content-hashed</p>
                    <p className="font-semibold text-stone-900" data-testid="usage-deduplicated">{usage.deduplicated_assets}</p>
                  </div>
                </div>
                <p className="text-xs text-stone-400 mt-2">New originals stored in {usage.media_folder}</p>
              </>
            ) : (
              <p className="text-sm text-stone-500">Loading usage...</p>
            )}
          </CardContent>
        </Card>

        {/* Recovered member artwork — full review queue lives on its own page */}
        {(orphanedGroups?.unfiltered_total_images ?? 0) > 0 && (
          <Card className="mb-6 border-cafa-gold/60" data-testid="orphaned-galleries-card">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium text-stone-800 text-sm flex items-center gap-2">
                  <UserSearch size={16} /> Recovered member artwork needs review
                </p>
                <p className="text-xs text-stone-500 mt-1">
                  {orphanedGroups.unfiltered_total_images} image(s) across {orphanedGroups.unfiltered_total_groups} folder(s)
                  are waiting to be matched to a member.
                </p>
              </div>
              <Link to="/admin/recover-artwork">
                <Button data-testid="go-to-recover-artwork-btn">Open Recover Artwork</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Duplicates */}
        <Card className="mb-6">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium text-stone-800 text-sm">Duplicate images</p>
              <p className="text-xs text-stone-500">
                Report images already tracked in the library that are byte-for-byte identical.
                Read-only — nothing is uploaded, renamed, moved or deleted.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={runDuplicates} disabled={checkingDupes} data-testid="media-duplicates-btn">
                {checkingDupes ? <><Loader2 size={15} className="mr-1 animate-spin" /> Checking...</> : <><Copy size={15} className="mr-1" /> Duplicate Report</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {duplicates && (
          <Card className="mb-6" data-testid="media-duplicate-report">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Duplicate Report — Review Before Merging</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div><p className="text-xs text-stone-500">Duplicate groups</p><p className="font-semibold" data-testid="dupe-groups">{duplicates.duplicate_groups}</p></div>
                <div><p className="text-xs text-stone-500">Assets involved</p><p className="font-semibold">{duplicates.duplicate_assets}</p></div>
                <div><p className="text-xs text-stone-500">Recoverable if merged</p><p className="font-semibold">{formatBytes(duplicates.reclaimable_bytes)}</p></div>
              </div>
              <p className="text-xs text-stone-500 mb-3">
                The copy with the green border is the recommended keeper (used in the most places, or the
                oldest upload if none are used anywhere). Merging repoints every reference to it, then
                stops tracking the others. Nothing is merged until you click "Merge This Group".
              </p>
              <div className="space-y-3">
                {duplicates.groups.map((g) => (
                  <div key={g.etag} className="border border-stone-200 rounded p-2" data-testid={`dupe-group-${g.etag}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-stone-500">{g.count} copies · {formatBytes(g.bytes_each)} each</p>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => mergeGroup(g.etag)}
                        disabled={!!merging[g.etag]}
                        data-testid={`merge-group-btn-${g.etag}`}
                      >
                        {merging[g.etag] ? <><Loader2 size={13} className="mr-1 animate-spin" /> Merging...</> : 'Merge This Group'}
                      </Button>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {g.assets.map((a) => (
                        <div key={a.public_id} className="w-24">
                          <img
                            src={mediaUrl(a, 'thumb')}
                            alt={a.public_id}
                            className={`w-24 h-24 object-cover rounded border-2 ${a.public_id === g.suggested_keeper_public_id ? 'border-emerald-500' : 'border-stone-200'}`}
                            loading="lazy"
                          />
                          <p className="text-[10px] text-stone-400 truncate" title={a.public_id}>{a.public_id.split('/').pop()}</p>
                          <p className="text-[10px] text-stone-500">
                            {a.reference_count} ref{a.reference_count !== 1 ? 's' : ''}
                            {a.public_id === g.suggested_keeper_public_id && <span className="text-emerald-600 font-medium"> · keep</span>}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {duplicates.duplicate_groups === 0 && (
                  <p className="text-sm text-stone-500">No duplicates detected among the tracked assets.</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Library */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Images size={17} /> Library ({total})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4 flex-wrap items-center">
              <div className="relative flex-1 max-w-sm">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                  placeholder="Search by filename, purpose or public ID..."
                  className="pl-9"
                  data-testid="media-search-input"
                />
              </div>
              <Button variant="outline" onClick={runSearch} disabled={loading} data-testid="media-search-btn">
                Search
              </Button>
              <div className="flex items-center gap-2 ml-auto">
                <label className="text-xs text-stone-500">Sort by</label>
                <select
                  value={sort}
                  onChange={(e) => changeSort(e.target.value)}
                  className="h-9 text-xs border border-stone-200 rounded px-2 bg-white"
                  data-testid="media-sort-select"
                >
                  <option value="date_desc">Newest upload first</option>
                  <option value="date_asc">Oldest upload first</option>
                </select>
              </div>
            </div>

            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-stone-50 border border-stone-200 rounded-lg" data-testid="media-selection-toolbar">
                <span className="text-xs font-medium text-stone-700">{selectedIds.length} selected</span>
                <Button size="sm" variant="ghost" className="text-xs h-8" onClick={() => setSelectedIds([])} data-testid="media-clear-selection">
                  Clear
                </Button>
                <MemberPicker authHeaders={authHeaders} selected={assignMember} onPick={setAssignMember} testId="media-assign-member-search" />
                <Button size="sm" disabled={!assignMember || assigning} onClick={assignSelected} data-testid="media-assign-btn">
                  {assigning ? <><Loader2 size={13} className="mr-1 animate-spin" /> Assigning...</> : `Assign to member`}
                </Button>
              </div>
            )}

            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {assets.map((asset) => {
                const picked = selectedIds.includes(asset.id);
                return (
                  <div key={asset.id} className="relative group border border-stone-200 rounded overflow-hidden" data-testid={`media-library-asset-${asset.id}`}>
                    <button
                      onClick={() => toggleSelect(asset.id)}
                      className={`block w-full ${picked ? 'ring-2 ring-cafa-gold' : ''}`}
                      data-testid={`media-select-${asset.id}`}
                    >
                      <img src={mediaUrl(asset, 'thumb')} alt={asset.original_filename || asset.public_id} className="w-full h-24 object-cover" loading="lazy" />
                    </button>
                    {picked && (
                      <CheckCircle2 size={16} className="absolute top-1 left-1 text-white bg-cafa-gold rounded-full" />
                    )}
                    {asset.claimed && !picked && (
                      <span className="absolute top-1 left-1 text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded" data-testid={`media-claimed-badge-${asset.id}`}>
                        Assigned
                      </span>
                    )}
                    <button
                      onClick={() => setZoom(asset)}
                      className="absolute top-1 right-1 bg-white/90 rounded p-1 text-stone-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="View full size"
                      data-testid={`media-zoom-${asset.id}`}
                    >
                      <ZoomIn size={13} />
                    </button>
                    <div className="p-1.5">
                      <p className="text-[11px] text-stone-700 truncate" title={asset.original_filename || asset.public_id}>
                        {asset.original_filename || asset.public_id.split('/').pop()}
                      </p>
                      <p className="text-[10px] text-stone-400">
                        {asset.upload_date ? asset.upload_date.slice(0, 10) : formatBytes(asset.bytes)}
                        {asset.purpose ? ` · ${asset.purpose}` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
              {!loading && assets.length === 0 && (
                <p className="col-span-full text-sm text-stone-500 py-8 text-center">
                  Nothing tracked yet. Images will appear here as they are uploaded across the site.
                </p>
              )}
            </div>

            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-stone-100" data-testid="media-pagination">
                <p className="text-xs text-stone-500" data-testid="media-pagination-label">
                  Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => goToPage(Math.max(0, offset - PAGE_SIZE))}
                    disabled={loading || offset === 0}
                    data-testid="media-page-prev-btn"
                  >
                    Back
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => goToPage(offset + PAGE_SIZE)}
                    disabled={loading || offset + PAGE_SIZE >= total}
                    data-testid="media-page-next-btn"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
          <DialogContent className="max-w-3xl" data-testid="media-zoom-dialog">
            {zoom && (
              <div>
                <DialogTitle className="sr-only">{zoom.original_filename || 'Media asset preview'}</DialogTitle>
                <DialogDescription className="sr-only">Full-size preview of the selected media library asset</DialogDescription>
                <img src={mediaUrl(zoom, 'full')} alt={zoom.original_filename || zoom.public_id}
                  className="w-full max-h-[70vh] object-contain rounded" />
                <p className="text-xs text-stone-500 mt-3">
                  {zoom.original_filename || zoom.public_id}
                  {zoom.upload_date ? ` · uploaded ${zoom.upload_date.slice(0, 10)}` : ''}
                  {zoom.purpose ? ` · ${zoom.purpose}` : ''}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminMedia;
